import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from 'use-intl';

/**
 * A sheet of paper, shown whole on a screen narrower than the paper.
 *
 * Reports are laid out in millimetres and that is not negotiable: D-034 keeps
 * one renderer for the screen, the printer and the PDF, because "a report that
 * differs from its preview is a report nobody trusts twice". A4 portrait is
 * 794px and landscape 1123px, against a 390px phone — so the preview was a
 * sideways scroller showing about a third of a page.
 *
 * Scaling is the way out that changes nothing. The sheet keeps its exact
 * millimetre size; a wrapper scales what is drawn, so print and PDF output are
 * byte-identical and the coach can see the whole page at a glance. Full size is
 * one tap away for reading the small print.
 *
 * **The print reset is not optional.** `styles.css` neutralises transforms on
 * `*:has(.report-preview)` — the sheet's *ancestors*. This wrapper sits inside
 * `.report-preview`, so it matches neither that rule nor the one that hides
 * everything else, and without its own rule it would keep the transform and
 * print a shrunken page. The rule lives beside the class in `styles.css`.
 */
export function PaperPreview({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const t = useTranslations();
  const frame = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [full, setFull] = useState(false);

  useEffect(() => {
    const box = frame.current;
    const paper = sheet.current;
    if (!box || !paper) return;

    const measure = () => {
      if (full) {
        setScale(1);
        return;
      }
      // `scrollWidth`, so the paper reports the width it wants rather than the
      // width it has been squeezed into.
      const paperWidth = paper.scrollWidth;
      const available = box.clientWidth;
      setScale(paperWidth > 0 && available > 0 ? Math.min(1, available / paperWidth) : 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(paper);
    return () => observer.disconnect();
  }, [full, children]);

  const scaled = scale < 1 && !full;

  return (
    // `min-w-0` and `max-w-full` are load-bearing: a grid or flex item defaults
    // to `min-width: auto`, so without them the 1123px sheet pushes this box
    // wider than the screen and the measurement below concludes it has room.
    // It worked on the call-up only because that preview happened to sit inside
    // a `Panel`, which clips. A primitive cannot depend on its parent for that.
    <div className={`min-w-0 max-w-full ${className}`}>
      {scaled || full ? (
        <div className="flex flex-wrap items-center gap-3 px-4 pt-3">
          <button
            type="button"
            onClick={() => setFull((v) => !v)}
            className="inline-flex min-h-11 items-center text-meta text-ink-muted underline decoration-line-strong underline-offset-2 transition-colors hover:text-ink hover:decoration-ink"
          >
            {full ? t('report.fitToWidth') : t('report.viewFullSize')}
          </button>
          {scaled ? (
            <span className="tabular text-meta text-ink-faint">
              {t('report.shownAt', { percent: Math.round(scale * 100) })}
            </span>
          ) : null}
        </div>
      ) : null}

      <div ref={frame} className={`w-full ${full ? 'overflow-x-auto' : 'overflow-hidden'}`}>
        <div
          ref={sheet}
          className="paper-fit w-fit origin-top-left rtl:origin-top-right"
          style={
            scaled
              ? {
                  transform: `scale(${scale})`,
                  // A scaled box still occupies its unscaled height, which
                  // would leave a long gap under a page shown at 49%.
                  marginBottom: `calc(${scale - 1} * ${sheet.current?.offsetHeight ?? 0}px)`,
                }
              : undefined
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}
